import { GraphQLErrorCodes } from '../../constants/auth-routes';
import { AuthAudience } from '../../constants/cookie-names';
import { TIME } from '../../constants/time';

/**
 * Shared MFA challenge and session-proof contract.
 *
 * Identity/auth/session owns this contract for every sibling MFA task.
 * Factor engines, login adapters, and UI consume these types; they do not
 * invent parallel challenge writers, client-header proofs, or audit sinks.
 */

export const MFA_PRIMARY_AUTH_KIND = {
  Authenticated: 'authenticated',
  MfaChallenge: 'mfa_challenge',
} as const;
export type MfaPrimaryAuthKind = (typeof MFA_PRIMARY_AUTH_KIND)[keyof typeof MFA_PRIMARY_AUTH_KIND];

export const MFA_FACTOR = {
  Totp: 'totp',
  Email: 'email',
  Passkey: 'passkey',
  Sms: 'sms',
} as const;
export type MfaFactor = (typeof MFA_FACTOR)[keyof typeof MFA_FACTOR];
export const MFA_FACTORS = [MFA_FACTOR.Totp, MFA_FACTOR.Email, MFA_FACTOR.Passkey, MFA_FACTOR.Sms] as const;

/**
 * Offers SMS as a new MFA method; off wherever the flag has not been created. Accounts that already have SMS keep
 * using, changing and removing it either way.
 */
export const SMS_MFA_FEATURE_FLAG = 'sms_mfa';

/** @deprecated SMS is an enrolled factor. Kept empty for migration callers. */
export const MFA_UNAVAILABLE_FACTOR = {} as const;

export const MFA_SMS_ABUSE_STATE = {
  Unlocked: 'UNLOCKED',
  Locked: 'LOCKED',
  ReviewNeeded: 'REVIEW_NEEDED',
} as const;
export type MfaSmsAbuseState = (typeof MFA_SMS_ABUSE_STATE)[keyof typeof MFA_SMS_ABUSE_STATE];

/** Backup-code recovery is not an enrolled login choice. */
export const MFA_RECOVERY_METHOD = 'recovery' as const;

export const MFA_FACTOR_STATE = {
  Pending: 'pending',
  Active: 'active',
} as const;
export type MfaFactorState = (typeof MFA_FACTOR_STATE)[keyof typeof MFA_FACTOR_STATE];

export const MFA_CHALLENGE_PURPOSE = {
  Login: 'login',
  Enrollment: 'enrollment',
  Reauth: 'reauth',
  Recovery: 'recovery',
} as const;
export type MfaChallengePurpose = (typeof MFA_CHALLENGE_PURPOSE)[keyof typeof MFA_CHALLENGE_PURPOSE];
export const MFA_CHALLENGE_PURPOSES = [
  MFA_CHALLENGE_PURPOSE.Login,
  MFA_CHALLENGE_PURPOSE.Enrollment,
  MFA_CHALLENGE_PURPOSE.Reauth,
  MFA_CHALLENGE_PURPOSE.Recovery,
] as const;

export const MFA_SANITIZED_ERROR_CODE = {
  ChallengeExpired: 'mfa.challenge_expired',
  ChallengeConsumed: 'mfa.challenge_consumed',
  InvalidCode: 'mfa.invalid_code',
  WrongAudience: 'mfa.wrong_audience',
  WrongPurpose: 'mfa.wrong_purpose',
  WrongUser: 'mfa.wrong_user',
  WrongSession: 'mfa.wrong_session',
  FactorNotEnrolled: 'mfa.factor_not_enrolled',
  EmailNotEnrolled: 'mfa.email_not_enrolled',
  TooManyAttempts: 'mfa.too_many_attempts',
  /** Another code can't be sent yet. Unlike TooManyAttempts, the challenge stays open for a code already sent. */
  ResendCooldown: 'mfa.resend_cooldown',
  ReplayedChallenge: 'mfa.replayed_challenge',
  DeliveryFailed: 'mfa.delivery_failed',
  FactorBlocked: 'mfa.factor_blocked',
  /** The method is not offered for new setups (SMS while its feature flag is off). */
  FactorUnavailable: 'mfa.factor_unavailable',
} as const;
export type MfaSanitizedErrorCode = (typeof MFA_SANITIZED_ERROR_CODE)[keyof typeof MFA_SANITIZED_ERROR_CODE];

export const MFA_FORBIDDEN_CLIENT_PROOF_HEADER = 'x-mfa-verified';

export const MFA_CHALLENGE_CONTRACT = {
  integrationOwner: 'identity-auth-session',
  proofMechanism: 'server-side-redis-assertion',
  proofKeying: 'sha256-access-token-digest',
  neverClientHeaders: true,
  forbiddenClientProofHeader: MFA_FORBIDDEN_CLIENT_PROOF_HEADER,
  emailRequiresExplicitEnrollment: true,
  verifiedEmailIsNotEnrollment: true,
  smsUnavailable: false,
  smsRequiresExplicitEnrollment: true,
  noMandatoryRolePolicy: true,
  completion: 'one-time-atomic',
  challengeTtlSeconds: 5 * TIME.SECONDS.PER_MINUTE,
  maxAttempts: 3,
  /** Lives as long as the access token; refresh carries MFA forward through the refresh token's `mfa` claim. */
  sessionProofTtlSeconds: 15 * TIME.SECONDS.PER_MINUTE,
  graphqlChallengeCode: GraphQLErrorCodes.MFA_CHALLENGE_REQUIRED,
} as const;

export const MFA_SESSION_PROOF_CONTRACT = {
  store: 'redis',
  keyTemplate: 'mfa_verified:{sha256(accessToken)}',
  setOnlyAfterSuccessfulVerification: true,
  failClosedOnStoreUnavailable: true,
  clientHeader: null,
} as const;

/**
 * Authenticator TOTP factor engine. Challenge completion, tokens,
 * and session-proof writes stay with Identity/auth/session.
 */
export const MFA_TOTP_CONTRACT = {
  factor: MFA_FACTOR.Totp,
  periodSeconds: 30,
  epochToleranceSeconds: 60,
  digits: 6,
  algorithm: 'sha1',
  issuer: 'StartHub',
  qrReturnedOnlyDuringEnrollment: true,
  neverLogProvisioningMaterial: true,
  noTokenIssuance: true,
  noSessionProofAuthority: true,
} as const;

/**
 * Email OTP factor engine. Challenge completion, tokens, and
 * session-proof writes stay with Identity/auth/session. Notification/template
 * delivery stays with SendMfaCodeEmailHandler.
 */
export const MFA_EMAIL_OTP_CONTRACT = {
  factor: MFA_FACTOR.Email,
  digits: 6,
  ttlSeconds: MFA_CHALLENGE_CONTRACT.challengeTtlSeconds,
  maxAttempts: MFA_CHALLENGE_CONTRACT.maxAttempts,
  maxSendsPerWindow: 5,
  resendIntervalSeconds: 30,
  hashedVerifier: 'sha256-hex',
  noTokenIssuance: true,
  noSessionProofAuthority: true,
  loginRequiresActiveEnrollment: true,
  pendingEnrollmentVerifiesCandidateWithoutLoginProof: true,
  neverLogLiveOtp: true,
  neverPersistLiveOtp: true,
} as const;

/**
 * Passkey WebAuthn factor engine. Challenge completion, tokens, and
 * session-proof writes stay with Identity/auth/session. Ceremony verify never
 * mints tokens or writes MFA session proofs.
 */
export const MFA_PASSKEY_CONTRACT = {
  factor: MFA_FACTOR.Passkey,
  library: '@simplewebauthn/server',
  userVerification: 'required',
  residentKey: 'preferred',
  authenticatorAttachment: 'platform-or-cross-platform',
  requireUserPresence: true,
  counterMustAdvanceOrStayEqual: true,
  transportsPersisted: true,
  multipleCredentialsPerUser: true,
  noTokenIssuance: true,
  noSessionProofAuthority: true,
  neverLogPrivateKeyMaterial: true,
  neverLogAttestationObject: true,
  pendingUntilCeremonyVerifyAndConfirm: true,
  loginRequiresActiveEnrollment: true,
  explicitFactorChoice: true,
  sharedRpIdForNewCredentials: true,
  exactOriginPerAudience: true,
  legacyHostRpIdUntilReplaced: true,
} as const;

/**
 * SMS OTP factor engine. Challenge completion, tokens, and
 * session-proof writes stay with Identity/auth/session. Telnyx Verify owns
 * code delivery/verification by default; a stub provider is used in tests.
 * Live SMS codes are never persisted locally when Verify owns check.
 *
 * Cal.com phone-verification budgets are starting configurable defaults, not
 * immutable product policy.
 */
export const MFA_SMS_OTP_CONTRACT = {
  factor: MFA_FACTOR.Sms,
  digits: 6,
  ttlSeconds: MFA_CHALLENGE_CONTRACT.challengeTtlSeconds,
  maxAttempts: MFA_CHALLENGE_CONTRACT.maxAttempts,
  defaultProvider: 'telnyx-verify',
  requestMaxPerWindow: 3,
  requestWindowSeconds: 60,
  requestBlockSeconds: 60,
  verifyMaxPerWindow: 3,
  verifyWindowSeconds: 60,
  budgetShortMax: 50,
  budgetShortWindowSeconds: 5 * TIME.SECONDS.PER_MINUTE,
  budgetLongMax: 250,
  budgetLongWindowSeconds: 30 * TIME.SECONDS.PER_DAY,
  abuseStates: MFA_SMS_ABUSE_STATE,
  noTokenIssuance: true,
  noSessionProofAuthority: true,
  loginRequiresActiveEnrollment: true,
  pendingUntilVerified: true,
  neverLogLiveOtp: true,
  neverPersistLiveOtp: true,
  neverLogFullPhone: true,
  explicitFactorChoice: true,
  neverAutomaticFallback: true,
  e164Normalized: true,
  notPhishingResistant: true,
  /** Deterministic stub Verify code for local/CI (never a live provider code). */
  testStubCode: '424242',
} as const;

export const MFA_REFRESH_CONTRACT = {
  restrictedChallengeCannotRefreshToFullAuth: true,
  refreshRequiresFullSession: true,
} as const;

export const MFA_FACTOR_TRANSITIONS = {
  enroll: { from: 'absent', to: MFA_FACTOR_STATE.Pending, requires: 'authenticated-session' },
  confirm: { from: MFA_FACTOR_STATE.Pending, to: MFA_FACTOR_STATE.Active, requires: 'factor-proof' },
  abandonExpires: true,
  disable: {
    from: MFA_FACTOR_STATE.Active,
    to: 'absent',
    requires: 'recent-reauth-and-existing-factor-proof',
  },
} as const;

/**
 * Concrete owners so storage, factor engines, login adapters, and UI can
 * proceed without a second source of truth. Audit events use the canonical security audit logger.
 */
export const MFA_CONTRACT_OWNERS = {
  integration: 'Identity/auth/session',
  storage: {
    owner: 'MfaService',
    sourceOfTruth: 'redis',
    keys: {
      settings: 'mfa:{userId}',
      pending: 'mfa_pending:{userId}',
      otp: 'mfa_code:{userId}',
      attempts: 'mfa_attempt:{userId}',
      sessionProof: 'mfa_verified:{sha256(accessToken)}',
      migrationCursor: 'mfa_migration:v2:cursor',
      migrationLock: 'mfa_migration:v2:lock',
      totpReplay: 'mfa_totp_replay:{userId}',
      totpChallengeAttempts: 'mfa_totp_attempt:{challengeId}',
      emailConsumed: 'mfa_email_consumed:{challengeId}',
      emailResend: 'mfa_email_resend:{userId}',
      emailResendLock: 'mfa_email_resend_lock:{userId}',
      emailChallengeAttempts: 'mfa_email_attempt:{challengeId}',
      passkeyRegistrationChallenge: 'mfa_passkey_reg:{userId}',
      passkeyAuthenticationChallenge: 'mfa_passkey_auth:{challengeId}',
      passkeyChallengeAttempts: 'mfa_passkey_attempt:{challengeId}',
      smsVerifySid: 'mfa_sms_verify:{challengeId}',
      smsRequestWindow: 'mfa_sms_request:{userId}',
      smsRequestBlock: 'mfa_sms_request_block:{userId}',
      smsVerifyWindow: 'mfa_sms_verify_window:{challengeId}',
      smsBudgetShort: 'mfa_sms_budget_short:{identityKey}',
      smsBudgetLong: 'mfa_sms_budget_long:{identityKey}',
      smsAbuseState: 'mfa_sms_abuse:{userId}',
      smsChallengeAttempts: 'mfa_sms_attempt:{challengeId}',
      reauth: 'mfa_reauth:{userId}:{sessionBindingId}',
      reauthChallenge: 'mfa_reauth_challenge:{userId}:{sessionBindingId}',
      loginChallenge: 'mfa_login_challenge:{userId}:{audience}',
    },
    path: 'apps/backend/src/infrastructure/security/providers/mfa.service.ts',
  },
  totpFactor: {
    owner: 'MfaTotpFactorService',
    path: 'apps/backend/src/infrastructure/security/providers/mfa-totp-factor.service.ts',
    noTokenIssuance: true,
    noSessionProofAuthority: true,
  },
  emailOtpFactor: {
    owner: 'MfaEmailOtpFactorService',
    path: 'apps/backend/src/infrastructure/security/providers/mfa-email-otp-factor.service.ts',
    noTokenIssuance: true,
    noSessionProofAuthority: true,
  },
  passkeyFactor: {
    owner: 'MfaPasskeyFactorService',
    path: 'apps/backend/src/infrastructure/security/providers/mfa-passkey-factor.service.ts',
    noTokenIssuance: true,
    noSessionProofAuthority: true,
  },
  smsOtpFactor: {
    owner: 'MfaSmsOtpFactorService',
    path: 'apps/backend/src/infrastructure/security/providers/mfa-sms-otp-factor.service.ts',
    noTokenIssuance: true,
    noSessionProofAuthority: true,
  },
  smsDelivery: {
    owner: 'IMfaSmsVerifyProvider',
    defaultProvider: 'telnyx-verify',
    testProvider: 'stub',
    path: 'apps/backend/src/infrastructure/security/providers/mfa-sms-verify.provider.ts',
  },
  encryption: {
    owner: 'EncryptionService',
    path: 'apps/backend/src/infrastructure/security/providers/encryption.service.ts',
  },
  loginCookies: {
    owner: 'BetterAuthService',
    csrf: 'Better Auth session cookie; existing audience-scoped CSRF remains for GraphQL mutations',
    path: 'apps/backend/src/domains/identity/better-auth/application/services/better-auth.service.ts',
    writers: ['apps/backend/src/domains/identity/better-auth/infrastructure/providers/better-auth.provider.ts'],
  },
  sessionProofGuard: {
    owner: 'MfaGuard',
    path: 'apps/backend/src/infrastructure/security/guards/mfa.guard.impl.ts',
    proof: 'server-side Redis assertion; never client headers',
  },
  emailDelivery: {
    owner: 'SendMfaCodeEmailHandler',
    event: 'SendMfaCodeEvent',
    path: 'apps/backend/src/domains/notifications/email-notification/application/event-handlers/send-mfa-code-email.handler.ts',
  },
  sessionRevocation: {
    owner: 'AuthenticationService',
    path: 'apps/backend/src/domains/identity/application/services/authentication.application-service.ts',
    alsoClear: ['MFA challenge records', 'MFA session-proof keys'],
  },
  audit: {
    owner: 'SecurityAuditLoggerService',
    policy: 'canonical security audit event chain',
    path: 'apps/backend/src/domains/identity/infrastructure/services/security.audit.logger.service.ts',
    noParallelSink: true,
  },
  enrollment: {
    owner: 'MfaEnrollmentApplicationService',
    path: 'apps/backend/src/domains/identity/application/services/mfa-enrollment.application-service.ts',
    pendingUntilConfirm: true,
    noUi: true,
  },
  passwordLogin: {
    owner: 'BetterAuthService',
    path: 'apps/backend/src/domains/identity/better-auth/infrastructure/providers/better-auth.provider.ts',
    noTokensUntilMfa: true,
    noUi: true,
  },
  oauthLogin: {
    owner: 'BetterAuthService',
    path: 'apps/backend/src/domains/identity/better-auth/infrastructure/providers/better-auth.provider.ts',
    noTokensUntilMfa: true,
    noUi: true,
    providers: ['google', 'linkedin'] as const,
    noNewProviderEntryPoints: true,
  },
  recovery: {
    owner: 'BetterAuthService',
    path: 'apps/backend/src/domains/identity/better-auth/infrastructure/providers/better-auth.provider.ts',
    requiresRestrictedPrimaryAuthChallenge: true,
    noStandaloneLogin: true,
    hashedIndividualCodes: true,
    atomicConsumeWithChallenge: true,
    noTokenIssuanceUntilChallengeConsumed: true,
    noAutomaticEmailOrSupportBypass: true,
    noUi: true,
  },
  settingsUi: {
    owner: 'MfaSettingsSection',
    path: 'packages/ui/src/pages/settings-pages/security/mfa-settings-section.tsx',
    apps: ['admin', 'web', 'partner'] as const,
    pendingVsActive: true,
    separateTotpAndEmailEnrollment: true,
    emailNeverAutomaticFallback: true,
    smsNeverAutomaticFallback: true,
    noSms: false,
    noPasskeys: false,
    clearSecretsOnExit: true,
  },
  loginUi: {
    owner: 'LoginForm',
    path: 'packages/ui/src/fixtures/auth/components/Forms/LoginForm.tsx',
    apps: ['admin', 'web', 'partner'] as const,
    passwordAndOauth: true,
    noLoggedInStateBeforeMfa: true,
    activeFactorsPlusExplicitRecovery: true,
    emailFallbackIsExplicitForTotp: true,
    smsOnlyFallsBackToEmailOtp: true,
    clearSecretsOnExit: true,
    refreshAuthOnceAfterCompletion: true,
  },
  integrationTests: {
    owner: 'packages/testing Playwright',
    path: 'packages/testing/playwright/tests/mfa-login.spec.ts',
    apps: ['admin', 'web', 'partner'] as const,
    noTokensUntilMfa: true,
    passwordGoogleLinkedIn: true,
    bothFactors: true,
    noEmailBypass: true,
    recovery: true,
    settingsChanges: true,
  },
  runbook: {
    owner: 'docs/operations',
    path: 'docs/operations/mfa-recovery-runbook.md',
    restoreCanary: 'apps/backend/src/mfa-restore-canary.cli.ts',
    lostFactorAndEmailOutage: true,
    noProductionDeploy: true,
    auditDeferredToEng46AndEng101: true,
    noParallelAuditSink: true,
  },
} as const;

/**
 * Authenticated enrollment, factor change, and recent primary reauthentication.
 * Factor engines verify proofs; the login service issues challenges.
 */
export const MFA_ENROLLMENT_CONTRACT = {
  owner: 'Identity/auth/session',
  pendingUntilConfirm: true,
  abandonExpires: true,
  firstFactorRequires: 'authenticated-session',
  protectedChangeRequires: 'recent-reauth-and-existing-factor-proof',
  addEmailRequiresExistingFactorProof: true,
  lastFactorDisableRequiresExplicitConfirm: true,
  loginChoicesActiveOnly: true,
  canonicalEmailOnly: true,
  stolenSessionCannotBypassTotp: true,
  newlyVerifiedEmailCannotBypassTotp: true,
  noUi: true,
  reauthTtlSeconds: 10 * TIME.SECONDS.PER_MINUTE,
} as const;

/**
 * Shared admin/web/partner MFA settings UI. Enrollment, login challenges,
 * and OAuth/first-password flows retain their respective service owners.
 */
export const MFA_SETTINGS_UI_CONTRACT = {
  owner: 'packages/ui settings security',
  apps: ['admin', 'web', 'partner'] as const,
  pendingVsActive: true,
  separateTotpAndEmailEnrollment: true,
  recentReauth: true,
  qrAndManualSetup: true,
  emailConfirmationAndResend: true,
  changeDisableAndRecoveryDisplayRegen: true,
  emailIsAlternativeAfterEnrollmentNeverAutomaticFallback: true,
  clearSecretsOnExit: true,
  noFalseEnabledFromPending: true,
  refreshAfterChanges: true,
  noSmsOrPasskeys: false,
  passkeysSupported: true,
  smsUnavailable: false,
  smsSupported: true,
  smsNeverAutomaticFallback: true,
  oauthFirstPasswordUxRemainsEng113: true,
} as const;

/**
 * Shared admin/web/partner MFA login challenge UI. Password and OAuth adapters
 * share challenge completion; settings use the enrollment service.
 */
export const MFA_LOGIN_UI_CONTRACT = {
  owner: 'packages/ui login challenge',
  apps: ['admin', 'web', 'partner'] as const,
  consumePasswordAndOauthChallenges: true,
  noLoggedInStateBeforeMfa: true,
  preserveDestinationAndCookieIsolation: true,
  activeTotpEmailChoicesPlusExplicitRecovery: true,
  activePasskeyChoiceWhenEnrolled: true,
  activeSmsChoiceWhenEnrolled: true,
  neverAutomaticEmailFallback: true,
  neverAutomaticSmsFallback: true,
  resendCooldown: true,
  expiryRestartAndExhaustion: true,
  refreshAuthAndRedirectOnce: true,
  clearSecretsOnExit: true,
  noAuthMethodRedesign: true,
} as const;

/**
 * Cross-app MFA integration, restore canary, and recovery runbook.
 * Audit events use the canonical security audit logger. This contract does not authorize a
 * production deploy.
 */
export const MFA_INTEGRATION_CONTRACT = {
  owner: 'Identity/auth/session',
  apps: ['admin', 'web', 'partner'] as const,
  passwordGoogleLinkedIn: true,
  bothFactors: true,
  noEmailBypass: true,
  recovery: true,
  settingsChanges: true,
  noTokensUntilMfa: true,
  restoreCanary: true,
  lostFactorAndEmailOutageRunbook: true,
  auditDeferredToEng46AndEng101: true,
  noProductionDeploy: true,
  noParallelAuditSink: true,
} as const;

/**
 * Password login challenge execution. Factor engines verify proofs.
 * Session proof uses Redis assertions. OAuth adapters and backup-code recovery
 * consume the same restricted login challenge; recovery is not a standalone login.
 */
export const MFA_LOGIN_CONTRACT = {
  owner: 'Identity/auth/session',
  enrolledUsersNoTokensUntilMfa: true,
  nonEnrolledLoginUnchanged: true,
  completion: 'one-time-atomic',
  restrictedCannotRefreshToFullAuth: true,
  sessionProof: 'mfa_verified:{sha256(accessToken)}',
  revocationAlsoClearsChallengesAndProofs: true,
  noUi: true,
  noMandatoryRolePolicy: true,
} as const;

/**
 * Google and LinkedIn reuse the login challenge and completion path.
 * Connecting a provider retains account-link ownership and proof checks.
 */
export const MFA_OAUTH_CONTRACT = {
  owner: 'Identity/auth/session',
  providers: ['google', 'linkedin'] as const,
  reuseSharedLoginChallenge: true,
  enrolledUsersNoTokensUntilMfa: true,
  nonEnrolledLoginUnchanged: true,
  distinguishLoginFromConnect: true,
  distinguishLoginFromRecentReauth: true,
  resumeCorrectAppAfterOneTimeCompletion: true,
  cancellationDoesNotIssueTokensOrLinkIdentity: true,
  preserveEng176Linking: true,
  preserveStatePkceRedirect: true,
  noNewProviderEntryPoints: true,
  connectionUxRemainsEng113: true,
  noUi: true,
} as const;

/**
 * Atomic hashed backup-code recovery and regeneration. Completes the
 * restricted primary-auth challenge. Backup codes require secure entropy and
 * timing-safe comparison. Regeneration is owned
 * by enrollment and still requires recent reauth.
 */
export const MFA_RECOVERY_CONTRACT = {
  owner: 'Identity/auth/session',
  method: MFA_RECOVERY_METHOD,
  requiresRestrictedPrimaryAuthChallenge: true,
  notStandaloneLogin: true,
  hashedIndividualCodes: true,
  atomicConsumeWithChallenge: true,
  oneConcurrentWinner: true,
  timingSafeCompare: true,
  entropyFromEng101: true,
  rateLimited: true,
  purposeUserAudienceBound: true,
  regenerationRequiresRecentReauthAndExistingFactorOrRecoveryProof: true,
  regenerationAtomicallyInvalidatesOldSet: true,
  plaintextOnceOnly: true,
  neverLogsOrHistory: true,
  noAutomaticEmailOrSupportBypass: true,
  noLoginChoice: true,
  noUi: true,
} as const;

export type MfaAuthSessionTokens = {
  accessToken: string;
  refreshToken: string;
};

export type MfaFactorEnrollment = {
  factor: MfaFactor;
  state: MfaFactorState;
};

/**
 * Server-side challenge binding. Not returned to clients.
 * `sessionBindingId` is a digest of browser/session material (for example
 * device-token hash), never a live access token or client header.
 */
export type MfaChallengeBinding = {
  challengeId: string;
  userId: string;
  primaryAuthOccurrenceId: string;
  purpose: MfaChallengePurpose;
  audience: AuthAudience;
  sessionBindingId: string;
  expiresAt: string;
  remainingAttempts: number;
  /**
   * Credential session version that passed primary (password) authentication when
   * the challenge was issued. Completion must reject when the stored version has
   * advanced, so a password change/reset cannot be bypassed by finishing a
   * challenge that was started against the previous password.
   */
  sessionVersion?: number;
};

/** Public challenge payload for password and Google/LinkedIn consumers. */
export type MfaChallengeView = {
  challengeId: string;
  purpose: MfaChallengePurpose;
  audience: AuthAudience;
  expiresAt: string;
  remainingAttempts: number;
  enrolledFactorChoices: readonly MfaFactor[];
  primaryFactor?: MfaFactor;
};

export type AuthenticatedPrimaryAuthResult = {
  kind: typeof MFA_PRIMARY_AUTH_KIND.Authenticated;
  tokens: MfaAuthSessionTokens;
};

export type MfaChallengePrimaryAuthResult = {
  kind: typeof MFA_PRIMARY_AUTH_KIND.MfaChallenge;
  challenge: MfaChallengeView;
  errorCode: typeof GraphQLErrorCodes.MFA_CHALLENGE_REQUIRED;
};

export type PrimaryAuthResult = AuthenticatedPrimaryAuthResult | MfaChallengePrimaryAuthResult;

export type MfaPublicError = {
  errorCode: MfaSanitizedErrorCode;
};

export type MfaTotpProvisioning = {
  secret: string;
  otpauthUri: string;
};

export type VerifyMfaTotpFactorInput = {
  binding: MfaChallengeBinding;
  code: string;
  userId: string;
  purpose: MfaChallengePurpose;
  audience: AuthAudience;
  sessionBindingId: string;
  nowIso: string;
  nowEpochSeconds?: number;
};

export const MFA_EMAIL_OTP_DESTINATION_KIND = {
  Verified: 'verified',
  PendingCandidate: 'pending-candidate',
} as const;
export type MfaEmailOtpDestinationKind =
  (typeof MFA_EMAIL_OTP_DESTINATION_KIND)[keyof typeof MFA_EMAIL_OTP_DESTINATION_KIND];

export type DispatchMfaEmailOtpInput = {
  binding: MfaChallengeBinding;
  userId: string;
  purpose: MfaChallengePurpose;
  audience: AuthAudience;
  sessionBindingId: string;
  destinationEmail: string;
  firstName: string;
  nowIso: string;
};

export type VerifyMfaEmailOtpInput = {
  binding: MfaChallengeBinding;
  code: string;
  userId: string;
  purpose: MfaChallengePurpose;
  audience: AuthAudience;
  sessionBindingId: string;
  destinationEmail: string;
  nowIso: string;
};

export type MfaPasskeyRegistrationOptions = {
  optionsJson: string;
  challengeId: string;
};

export type MfaPasskeyAuthenticationOptions = {
  optionsJson: string;
  challengeId: string;
};

export type BeginMfaPasskeyRegistrationInput = {
  binding: MfaChallengeBinding;
  userId: string;
  userName: string;
  userDisplayName: string;
  audience: AuthAudience;
  origin: string;
  excludeCredentialIds: readonly string[];
};

export type VerifyMfaPasskeyRegistrationInput = {
  binding: MfaChallengeBinding;
  responseJson: string;
  userId: string;
  purpose: MfaChallengePurpose;
  audience: AuthAudience;
  sessionBindingId: string;
  origin: string;
  nowIso: string;
  credentialName?: string;
};

export type BeginMfaPasskeyAuthenticationInput = {
  binding: MfaChallengeBinding;
  userId: string;
  purpose: MfaChallengePurpose;
  audience: AuthAudience;
  sessionBindingId: string;
  origin: string;
  allowCredentialIds: readonly string[];
};

export type VerifyMfaPasskeyAuthenticationInput = {
  binding: MfaChallengeBinding;
  responseJson: string;
  userId: string;
  purpose: MfaChallengePurpose;
  audience: AuthAudience;
  sessionBindingId: string;
  origin: string;
  nowIso: string;
};

export type MfaPasskeyCredentialRecord = {
  credentialId: string;
  rpId: string;
  publicKey: string;
  counter: number;
  transports: readonly string[];
  deviceType: string;
  backedUp: boolean;
  name: string;
  enrolledAt: string;
  lastUsedAt?: string;
};

export type MfaPasskeyRegistrationSuccess = {
  ok: true;
  factor: typeof MFA_FACTOR.Passkey;
  userId: string;
  purpose: MfaChallengePurpose;
  challengeId: string;
  credential: MfaPasskeyCredentialRecord;
};

export type MfaPasskeyAuthenticationSuccess = {
  ok: true;
  factor: typeof MFA_FACTOR.Passkey;
  userId: string;
  purpose: MfaChallengePurpose;
  challengeId: string;
  credentialId: string;
  newCounter: number;
  rpId: string;
};

export type MfaEmailOtpDispatchSuccess = {
  ok: true;
  factor: typeof MFA_FACTOR.Email;
  challengeId: string;
  destinationKind: MfaEmailOtpDestinationKind;
};

export type MfaFactorVerificationSuccess = {
  ok: true;
  factor: MfaFactor;
  userId: string;
  purpose: MfaChallengePurpose;
  challengeId: string;
  acceptedTimeStep?: number;
  credentialId?: string;
  newCounter?: number;
  rpId?: string;
};

export type MfaFactorVerificationFailure = {
  ok: false;
  errorCode: MfaSanitizedErrorCode;
  retryAfterSeconds?: number;
};

export type MfaEmailOtpDispatchResult = MfaEmailOtpDispatchSuccess | MfaFactorVerificationFailure;

export type MfaFactorVerificationResult = MfaFactorVerificationSuccess | MfaFactorVerificationFailure;

export const MFA_SMS_OTP_DESTINATION_KIND = {
  PendingCandidate: 'pending-candidate',
  Enrolled: 'enrolled',
} as const;
export type MfaSmsOtpDestinationKind = (typeof MFA_SMS_OTP_DESTINATION_KIND)[keyof typeof MFA_SMS_OTP_DESTINATION_KIND];

export type DispatchMfaSmsOtpInput = {
  binding: MfaChallengeBinding;
  userId: string;
  purpose: MfaChallengePurpose;
  audience: AuthAudience;
  sessionBindingId: string;
  destinationPhoneE164: string;
  nowIso: string;
  identityKey?: string;
};

export type VerifyMfaSmsOtpInput = {
  binding: MfaChallengeBinding;
  code: string;
  userId: string;
  purpose: MfaChallengePurpose;
  audience: AuthAudience;
  sessionBindingId: string;
  destinationPhoneE164: string;
  nowIso: string;
};

export type MfaSmsOtpDispatchSuccess = {
  ok: true;
  factor: typeof MFA_FACTOR.Sms;
  challengeId: string;
  destinationKind: MfaSmsOtpDestinationKind;
  retryAfterSeconds?: number;
};

export type MfaSmsOtpDispatchResult = MfaSmsOtpDispatchSuccess | MfaFactorVerificationFailure;

export type EvaluateMfaChallengeCompletionInput = {
  binding: MfaChallengeBinding;
  nowIso: string;
  userId: string;
  purpose: MfaChallengePurpose;
  audience: AuthAudience;
  sessionBindingId: string;
  alreadyConsumed: boolean;
};

export type MfaChallengeCompletionDecision = { allowed: true } | { allowed: false; errorCode: MfaSanitizedErrorCode };

export function isMfaFactor(value: string): value is MfaFactor {
  return MFA_FACTORS.some(factor => factor === value);
}

export function isMfaRecoveryMethod(value: string): boolean {
  return value === MFA_RECOVERY_METHOD;
}

export function isMfaChallengePurpose(value: string): value is MfaChallengePurpose {
  return MFA_CHALLENGE_PURPOSES.some(purpose => purpose === value);
}

export function isMfaSanitizedErrorCode(value: string): value is MfaSanitizedErrorCode {
  return Object.values(MFA_SANITIZED_ERROR_CODE).some(code => code === value);
}

export function isAuthAudience(value: string): value is AuthAudience {
  return value === AuthAudience.Web || value === AuthAudience.Admin || value === AuthAudience.Partner;
}

function readRemainingAttempts(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
    return Math.floor(value);
  }
  if (typeof value === 'string' && /^\d+$/.test(value)) {
    return Number.parseInt(value, 10);
  }
  return undefined;
}

export function parseMfaChallengeView(value: unknown): MfaChallengeView | undefined {
  if (typeof value !== 'object' || value === null) {
    return undefined;
  }
  const record = value as Record<string, unknown>;
  const challengeId = record.challengeId;
  const purpose = record.purpose;
  const audience = record.audience;
  const expiresAt = record.expiresAt;
  const remainingAttempts = readRemainingAttempts(record.remainingAttempts);
  const factors = record.enrolledFactorChoices;
  if (typeof challengeId !== 'string' || challengeId.length === 0) {
    return undefined;
  }
  if (typeof purpose !== 'string' || purpose !== MFA_CHALLENGE_PURPOSE.Login) {
    return undefined;
  }
  if (typeof audience !== 'string' || !isAuthAudience(audience)) {
    return undefined;
  }
  if (typeof expiresAt !== 'string' || expiresAt.length === 0) {
    return undefined;
  }
  if (remainingAttempts === undefined) {
    return undefined;
  }
  if (!Array.isArray(factors)) {
    return undefined;
  }
  const enrolledFactorChoices = factors.filter(
    (factor): factor is MfaFactor => typeof factor === 'string' && isMfaFactor(factor),
  );
  const rawPrimary = record.primaryFactor;
  const primaryFactor =
    typeof rawPrimary === 'string' && isMfaFactor(rawPrimary) && enrolledFactorChoices.includes(rawPrimary)
      ? rawPrimary
      : enrolledFactorChoices[0];
  return {
    audience,
    challengeId,
    enrolledFactorChoices,
    expiresAt,
    primaryFactor,
    purpose: MFA_CHALLENGE_PURPOSE.Login,
    remainingAttempts,
  };
}

/** sessionStorage key that keeps a pending login challenge across reloads of the same tab. */
export const MFA_LOGIN_CHALLENGE_STORAGE_KEY = 'starthub.mfaLoginChallenge';

export const MFA_LOGIN_CHALLENGE_QUERY_KEYS = {
  Audience: 'audience',
  ChallengeId: 'challengeId',
  Code: 'code',
  ExpiresAt: 'expiresAt',
  Factors: 'factors',
  Flag: 'mfa_challenge',
  Primary: 'primary',
  Purpose: 'purpose',
  RemainingAttempts: 'remainingAttempts',
} as const;

export function parseMfaChallengeFromSearchParams(params: {
  get(name: string): string | null;
}): MfaChallengeView | undefined {
  if (params.get(MFA_LOGIN_CHALLENGE_QUERY_KEYS.Flag) !== 'true') {
    return undefined;
  }
  if (params.get(MFA_LOGIN_CHALLENGE_QUERY_KEYS.Code) !== GraphQLErrorCodes.MFA_CHALLENGE_REQUIRED) {
    return undefined;
  }
  const factors = (params.get(MFA_LOGIN_CHALLENGE_QUERY_KEYS.Factors) ?? '')
    .split(',')
    .filter(factor => factor.length > 0);
  return parseMfaChallengeView({
    audience: params.get(MFA_LOGIN_CHALLENGE_QUERY_KEYS.Audience),
    challengeId: params.get(MFA_LOGIN_CHALLENGE_QUERY_KEYS.ChallengeId),
    enrolledFactorChoices: factors,
    expiresAt: params.get(MFA_LOGIN_CHALLENGE_QUERY_KEYS.ExpiresAt),
    primaryFactor: params.get(MFA_LOGIN_CHALLENGE_QUERY_KEYS.Primary) ?? undefined,
    purpose: params.get(MFA_LOGIN_CHALLENGE_QUERY_KEYS.Purpose),
    remainingAttempts: params.get(MFA_LOGIN_CHALLENGE_QUERY_KEYS.RemainingAttempts),
  });
}

export function stripMfaLoginChallengeSearchParams(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const key of Object.values(MFA_LOGIN_CHALLENGE_QUERY_KEYS)) {
    next.delete(key);
  }
  return next;
}

export function loginFactorChoicesFromEnrollment(enrollment: readonly MfaFactorEnrollment[]): readonly MfaFactor[] {
  return MFA_FACTORS.filter(factor =>
    enrollment.some(entry => entry.factor === factor && entry.state === MFA_FACTOR_STATE.Active),
  );
}

export function isEmailOfferedAtLogin(choices: readonly MfaFactor[]): boolean {
  return choices.includes(MFA_FACTOR.Email);
}

/**
 * Login email OTP is available only after explicit email-factor enrollment.
 * A verified mailbox is not enrollment.
 */
export function isEmailFactorEnrolled(enrollment: readonly MfaFactorEnrollment[]): boolean {
  return enrollment.some(entry => entry.factor === MFA_FACTOR.Email && entry.state === MFA_FACTOR_STATE.Active);
}

export function isSmsOfferedAtLogin(choices: readonly MfaFactor[]): boolean {
  return choices.includes(MFA_FACTOR.Sms);
}

/**
 * Login SMS OTP is available only after explicit SMS-factor enrollment.
 */
export function isSmsFactorEnrolled(enrollment: readonly MfaFactorEnrollment[]): boolean {
  return enrollment.some(entry => entry.factor === MFA_FACTOR.Sms && entry.state === MFA_FACTOR_STATE.Active);
}

/**
 * Normalize phone to E.164 when the input is already a plausible E.164 string.
 * Returns undefined for invalid shapes. Does not invent country codes.
 */
export function normalizeMfaDestinationPhoneE164(value: string): string | undefined {
  const trimmed = value.trim();
  if (!/^\+[1-9]\d{7,14}$/.test(trimmed)) {
    return undefined;
  }
  return trimmed;
}

/** Mask E.164 for UI/logs: keep country-ish prefix and last 2 digits. */
export function maskMfaPhoneE164(e164: string): string {
  const normalized = normalizeMfaDestinationPhoneE164(e164);
  if (!normalized) {
    return '***';
  }
  if (normalized.length <= 4) {
    return '***';
  }
  const last = normalized.slice(-2);
  const prefix = normalized.slice(0, Math.min(3, normalized.length - 2));
  return `${prefix}${'*'.repeat(Math.max(normalized.length - prefix.length - 2, 2))}${last}`;
}

export function toMfaChallengeView(
  binding: MfaChallengeBinding,
  enrollment: readonly MfaFactorEnrollment[],
  primaryFactor?: MfaFactor,
): MfaChallengeView {
  const enrolledFactorChoices = loginFactorChoicesFromEnrollment(enrollment);
  const resolvedPrimary =
    primaryFactor && enrolledFactorChoices.includes(primaryFactor) ? primaryFactor : enrolledFactorChoices[0];
  return {
    challengeId: binding.challengeId,
    purpose: binding.purpose,
    audience: binding.audience,
    expiresAt: binding.expiresAt,
    remainingAttempts: binding.remainingAttempts,
    enrolledFactorChoices,
    primaryFactor: resolvedPrimary,
  };
}

export function primaryAuthResultIssuesSessionTokens(
  result: PrimaryAuthResult,
): result is AuthenticatedPrimaryAuthResult {
  return result.kind === MFA_PRIMARY_AUTH_KIND.Authenticated;
}

export function getPrimaryAuthSessionTokens(result: PrimaryAuthResult): MfaAuthSessionTokens | undefined {
  return primaryAuthResultIssuesSessionTokens(result) ? result.tokens : undefined;
}

export function evaluateMfaChallengeCompletion(
  input: EvaluateMfaChallengeCompletionInput,
): MfaChallengeCompletionDecision {
  if (input.userId !== input.binding.userId) {
    return { allowed: false, errorCode: MFA_SANITIZED_ERROR_CODE.WrongUser };
  }
  if (input.purpose !== input.binding.purpose) {
    return { allowed: false, errorCode: MFA_SANITIZED_ERROR_CODE.WrongPurpose };
  }
  if (input.audience !== input.binding.audience) {
    return { allowed: false, errorCode: MFA_SANITIZED_ERROR_CODE.WrongAudience };
  }
  if (input.sessionBindingId !== input.binding.sessionBindingId) {
    return { allowed: false, errorCode: MFA_SANITIZED_ERROR_CODE.WrongSession };
  }
  if (input.alreadyConsumed) {
    return { allowed: false, errorCode: MFA_SANITIZED_ERROR_CODE.ReplayedChallenge };
  }
  const nowMs = Date.parse(input.nowIso);
  const expiresMs = Date.parse(input.binding.expiresAt);
  if (!(Number.isFinite(nowMs) && Number.isFinite(expiresMs)) || nowMs >= expiresMs) {
    return { allowed: false, errorCode: MFA_SANITIZED_ERROR_CODE.ChallengeExpired };
  }
  if (input.binding.remainingAttempts <= 0) {
    return { allowed: false, errorCode: MFA_SANITIZED_ERROR_CODE.TooManyAttempts };
  }
  return { allowed: true };
}

export function isMfaFactorVerificationSuccess(
  result: MfaFactorVerificationResult,
): result is MfaFactorVerificationSuccess {
  return result.ok === true;
}

export function totpSecretStoreForPurpose(purpose: MfaChallengePurpose): MfaFactorState {
  return factorStateStoreForPurpose(purpose);
}

export function emailFactorStoreForPurpose(purpose: MfaChallengePurpose): MfaFactorState {
  return factorStateStoreForPurpose(purpose);
}

export function smsFactorStoreForPurpose(purpose: MfaChallengePurpose): MfaFactorState {
  return factorStateStoreForPurpose(purpose);
}

export function passkeyFactorStoreForPurpose(purpose: MfaChallengePurpose): MfaFactorState {
  return factorStateStoreForPurpose(purpose);
}

export function factorStateStoreForPurpose(purpose: MfaChallengePurpose): MfaFactorState {
  switch (purpose) {
    case MFA_CHALLENGE_PURPOSE.Enrollment:
      return MFA_FACTOR_STATE.Pending;
    case MFA_CHALLENGE_PURPOSE.Login:
    case MFA_CHALLENGE_PURPOSE.Reauth:
    case MFA_CHALLENGE_PURPOSE.Recovery:
      return MFA_FACTOR_STATE.Active;
    default: {
      const exhaustive: never = purpose;
      return exhaustive;
    }
  }
}

export function normalizeMfaDestinationEmail(email: string): string {
  return email.trim().toLowerCase();
}

export function isSanitizedMfaPublicError(payload: Record<string, unknown>): payload is MfaPublicError {
  const keys = Object.keys(payload);
  const [onlyKey] = keys;
  if (keys.length !== 1 || onlyKey !== 'errorCode' || typeof payload.errorCode !== 'string') {
    return false;
  }

  return isMfaSanitizedErrorCode(payload.errorCode);
}

export function createMfaChallengeBinding(overrides?: Partial<MfaChallengeBinding>): MfaChallengeBinding {
  return {
    challengeId: 'challenge-1',
    userId: '1001',
    primaryAuthOccurrenceId: 'primary-auth-1',
    purpose: MFA_CHALLENGE_PURPOSE.Login,
    audience: AuthAudience.Web,
    sessionBindingId: 'session-binding-1',
    expiresAt: '2026-09-15T12:05:00.000Z',
    remainingAttempts: MFA_CHALLENGE_CONTRACT.maxAttempts,
    sessionVersion: 0,
    ...overrides,
  };
}

export function createAuthenticatedPrimaryAuthResult(tokens?: MfaAuthSessionTokens): AuthenticatedPrimaryAuthResult {
  return {
    kind: MFA_PRIMARY_AUTH_KIND.Authenticated,
    tokens: tokens ?? { accessToken: 'access-token', refreshToken: 'refresh-token' },
  };
}

export function createMfaChallengePrimaryAuthResult(
  binding?: MfaChallengeBinding,
  enrollment: readonly MfaFactorEnrollment[] = [{ factor: MFA_FACTOR.Totp, state: MFA_FACTOR_STATE.Active }],
): MfaChallengePrimaryAuthResult {
  const resolvedBinding = binding ?? createMfaChallengeBinding();
  return {
    kind: MFA_PRIMARY_AUTH_KIND.MfaChallenge,
    challenge: toMfaChallengeView(resolvedBinding, enrollment),
    errorCode: GraphQLErrorCodes.MFA_CHALLENGE_REQUIRED,
  };
}
